import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { ItemButton } from './ItemButtons';

/**
 * Searchable grid of catalog items (services or products) for the POS.
 * Extracted from POSDashboard to remove duplicated services/products blocks.
 *
 * Props:
 *  - items: array of catalog items (services or products)
 *  - type: "service" | "product"
 *  - search: current search string
 *  - onSearchChange(str): setter for the search state
 *  - onAdd(item): add item to cart
 *  - isMasterAdmin: boolean, enables inline price editing on ItemButton
 *  - onPriceUpdate(item, newPrice, type): handler for inline price edit
 *  - placeholder: search input placeholder text
 *  - testIdSearch: data-testid for the search input
 */
export default function CatalogGrid({
  items,
  type,
  search,
  onSearchChange,
  onAdd,
  isMasterAdmin,
  onPriceUpdate,
  placeholder,
  testIdSearch,
}) {
  const query = (search || '').toLowerCase();
  const filtered = items.filter(it => it.name.toLowerCase().includes(query));
  const noun = type === 'service' ? 'servicios' : 'productos';

  return (
    <>
      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            type="text"
            placeholder={placeholder}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 pr-10 h-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
            data-testid={testIdSearch}
          />
          {search && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {isMasterAdmin && (
        <p className="text-xs text-zinc-600 mb-4">
          💡 Como Master Admin, puedes editar precios haciendo hover sobre cada item y clickeando el ícono de edición.
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {filtered.map(item => (
          <ItemButton
            key={item.id}
            item={item}
            type={type}
            onAdd={onAdd}
            isMasterAdmin={isMasterAdmin}
            onPriceUpdate={onPriceUpdate}
          />
        ))}
        {filtered.length === 0 && search && (
          <p className="col-span-full text-center text-zinc-500 py-8">
            No se encontraron {noun} para &quot;{search}&quot;
          </p>
        )}
      </div>
    </>
  );
}
